import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Sparkles, PenTool, Layers, Scissors, Heart, Eye, Droplets, Wrench, Star, Gem, Palette } from 'lucide-react';
export function Curriculum() {
  const modules = [{
    title: 'Anatomía de las uñas',
    desc: 'Conocé la estructura de la uña para trabajar con fundamento técnico.',
    icon: <Eye data-id="element-1" />
  }, {
    title: 'Microbiología básica',
    desc: 'Reconocer enfermedades. Microorganismos patógenos: bacterias, virus, hongos. Resolver situaciones de heridas/accidentes.',
    icon: <ShieldIcon data-id="element-2" />
  }, {
    title: 'Bioseguridad',
    desc: 'Esterilización química, procesos de descontaminación (todos los niveles). Implementos indispensables. Uso correcto de herramientas y productos aptos para la salud.',
    icon: <ShieldIcon data-id="element-3" />
  }, {
    title: 'Preparación de lámina ungeal',
    desc: 'Técnica profesional de preparación para máxima adherencia y durabilidad.',
    icon: <Scissors data-id="element-4" />
  }, {
    title: 'Manicuría básica / universal',
    desc: 'Dominio de la técnica clásica como base fundamental.',
    icon: <Heart data-id="element-5" />
  }, {
    title: 'Manicuría combinada con torno/drill',
    desc: 'Técnica avanzada combinando herramientas manuales y eléctricas.',
    icon: <Wrench data-id="element-6" />
  }, {
    title: 'Esmaltado Semipermanente Perfecto',
    desc: 'Aplicación impecable para un acabado duradero y profesional.',
    icon: <Droplets data-id="element-7" />
  }, {
    title: 'Remoción segura',
    desc: 'Remoción de producto sin dañar la lámina natural.',
    icon: <Heart data-id="element-8" />
  }, {
    title: 'Hard Gel & Capping',
    desc: 'Tratamientos de nivelación, balance y refuerzo.',
    icon: <Layers data-id="element-9" />
  }, {
    title: 'Técnicas híbridas',
    desc: 'Baby glitter. Abrir canales de adherencia para duración prolongada. Administrar productos correctamente.',
    icon: <Sparkles data-id="element-10" />
  }, {
    title: 'Pinceles y productos',
    desc: 'Qué pinceles usar para no tener gastos innecesarios.',
    icon: <PenTool data-id="element-11" />
  }, {
    title: 'Puntas de salón',
    desc: 'Las de mayor demanda: Square, Coffin y Stiletto.',
    icon: <Gem data-id="element-12" />
  }, {
    title: 'Corte y colocación de molde',
    desc: 'Correcta colocación de acuerdo a cada estructura, con acrílico, gel, polygel, etc.',
    icon: <Scissors data-id="element-13" />
  }, {
    title: 'Encapsulado Perfecto',
    desc: 'Técnica profesional de encapsulado con acabado impecable.',
    icon: <Layers data-id="element-14" />
  }, {
    title: 'Técnica de reversa',
    desc: 'Método avanzado para diseños con profundidad y detalle.',
    icon: <Sparkles data-id="element-15" />
  }, {
    title: 'Técnica Baby Boomer',
    desc: 'Degradé suave y profesional, uno de los más pedidos.',
    icon: <Palette data-id="element-16" />
  }, {
    title: 'Técnicas de limado',
    desc: 'Limado profesional para un acabado fino y uniforme.',
    icon: <Wrench data-id="element-17" />
  }, {
    title: 'Uso de torno/drill + fresas',
    desc: 'Manejo profesional de torno y fresas para acabado de estudio.',
    icon: <Wrench data-id="element-18" />
  }, {
    title: 'Esmaltado sobre esculpidas',
    desc: 'Semipermanente perfecto sobre uñas esculpidas. Remoción de producto y estructura.',
    icon: <Droplets data-id="element-19" />
  }];
  const bonusModules = [{
    title: 'Técnica Press On',
    desc: 'Creación de sets desmontables, reutilizables y en tendencia. Toma de medidas a distancia para trabajar sin la presencia de la clienta.',
    icon: <BookOpen data-id="element-20" />
  }, {
    title: 'Soft Gel / Dipping',
    desc: 'Extensiones de alta durabilidad y acabado profesional.',
    icon: <Layers data-id="element-21" />
  }, {
    title: 'Nail Art: de cero a experta',
    desc: 'Dibujo a mano alzada desde la base, diseño de cartoons y Flowers 3D. Técnicas que marcan la diferencia y elevan el nivel artístico.',
    icon: <Palette data-id="element-22" />
  }];
  return <section className="py-20 bg-white relative" data-id="element-23">
      <div className="max-w-6xl mx-auto px-4" data-id="element-24">
        <div className="text-center mb-16" data-id="element-25">
          <h2 className="text-3xl md:text-5xl font-bold text-[#1A1A2E] mb-4" data-id="element-26">
            ¿Qué vas a aprender?
          </h2>
          <p className="text-xl text-gray-600" data-id="element-27">
            Vas a trabajar técnica real, no "tips sueltos".
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-28">
          {modules.map((mod, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.03
        }} className="group p-6 rounded-xl border border-gray-100 bg-white hover:border-[#C9A96E]/50 hover:shadow-lg transition-all duration-300" data-id="element-29">
              <div className="w-12 h-12 bg-[#FFF8F0] rounded-full flex items-center justify-center text-[#9B2C5E] mb-4 group-hover:bg-[#9B2C5E] group-hover:text-white transition-colors" data-id="element-30">
                {mod.icon}
              </div>
              <h3 className="text-lg font-bold text-[#1A1A2E] mb-2" data-id="element-31">
                {mod.title}
              </h3>
              <p className="text-gray-500 text-sm" data-id="element-32">{mod.desc}</p>
            </motion.div>)}
        </div>

        {/* BONUS Section */}
        <div className="mt-16" data-id="element-33">
          <div className="text-center mb-10" data-id="element-34">
            <span className="inline-block bg-[#C9A96E]/10 border border-[#C9A96E]/30 rounded-full px-5 py-2 mb-4" data-id="element-35">
              <span className="text-[#C9A96E] font-bold tracking-widest text-sm uppercase" data-id="element-36">
                ✦ BONUS – Nivel Especial ✦
              </span>
            </span>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-37">
              Ideal para quienes desean trabajar también desde casa, aprendiendo
              a tomar medidas sin que la clienta esté presente.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-38">
            {bonusModules.map((mod, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.05
          }} className="group p-6 rounded-xl border-2 border-[#C9A96E]/30 bg-[#FFF8F0] hover:border-[#C9A96E] hover:shadow-lg transition-all duration-300" data-id="element-39">
                <div className="w-12 h-12 bg-[#C9A96E]/20 rounded-full flex items-center justify-center text-[#C9A96E] mb-4 group-hover:bg-[#C9A96E] group-hover:text-white transition-colors" data-id="element-40">
                  {mod.icon}
                </div>
                <h3 className="text-lg font-bold text-[#1A1A2E] mb-2" data-id="element-41">
                  {mod.title}
                </h3>
                <p className="text-gray-500 text-sm" data-id="element-42">{mod.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-[#C9A96E] to-transparent opacity-30 mt-20" data-id="element-43" />
    </section>;
}
// Helper icon component
function ShieldIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-44">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" data-id="element-45" />
    </svg>;
}